import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { motion, AnimatePresence } from 'framer-motion';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import EmailIcon from '@mui/icons-material/Email';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { SocialMedia } from '../../constants/links';

const navItems = [
  { label: 'About', href: '#about' },
  { label: 'Organizations', href: '#organizations' },
  { label: 'Works', href: '#works' },
  { label: 'Skills', href: '#skills' },
];

const socialLinks = [
  { icon: <LinkedInIcon />, href: SocialMedia.LINKED_IN, label: 'LinkedIn' },
  { icon: <GitHubIcon />, href: SocialMedia.GITHUB, label: 'GitHub' },
  { icon: <EmailIcon />, href: SocialMedia.MAIL_TO, label: 'Email' },
];

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Desktop Navigation */}
      <Box
        component={motion.nav}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          padding: { xs: '16px 20px', md: '20px 40px' },
          transition: 'all 0.3s ease',
          background: isScrolled 
            ? 'rgba(10, 10, 15, 0.8)' 
            : 'transparent',
          backdropFilter: isScrolled ? 'blur(20px)' : 'none',
          borderBottom: isScrolled 
            ? '1px solid rgba(68, 212, 130, 0.1)' 
            : 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            maxWidth: '1400px',
            margin: '0 auto',
          }}
        >
          {/* Logo / Brand */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Typography
              sx={{
                fontFamily: 'Fira Code, monospace',
                fontSize: { xs: '1rem', md: '1.2rem' },
                fontWeight: 500,
                color: '#44D482',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <span style={{ color: 'rgba(255,255,255,0.6)' }}>$</span>
              {' '}abhishek
              <span className="terminal-cursor">_</span>
            </Typography>
          </motion.div>

          {/* Desktop Nav Links */}
          <Stack
            direction="row"
            spacing={4}
            sx={{
              display: { xs: 'none', md: 'flex' },
            }}
          >
            {navItems.map((item, index) => (
              <motion.a
                key={item.label}
                href={item.href}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                style={{
                  textDecoration: 'none',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Space Grotesk, sans-serif',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    color: 'rgba(255, 255, 255, 0.7)',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    '&:hover': {
                      color: '#44D482',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: '-4px',
                      left: 0,
                      width: '0%',
                      height: '2px',
                      background: '#44D482',
                      transition: 'width 0.3s ease',
                    },
                    '&:hover::after': {
                      width: '100%',
                    },
                  }}
                >
                  {item.label}
                </Typography>
              </motion.a>
            ))}
          </Stack>

          {/* Social Icons - Desktop */}
          <Stack
            direction="row"
            spacing={1}
            sx={{
              display: { xs: 'none', md: 'flex' },
            }}
          >
            {socialLinks.map((social, index) => (
              <motion.div
                key={social.label}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.5 + index * 0.1 }}
              >
                <IconButton
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: '#44D482',
                      background: 'rgba(68, 212, 130, 0.1)',
                    },
                  }}
                >
                  {social.icon}
                </IconButton>
              </motion.div>
            ))}
          </Stack>

          {/* Mobile Menu Button */}
          <IconButton
            onClick={() => setMobileMenuOpen(true)}
            sx={{
              display: { xs: 'flex', md: 'none' },
              color: '#44D482',
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2000,
              background: 'rgba(10, 10, 15, 0.95)',
              backdropFilter: 'blur(20px)',
            }}
          >
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '40px',
              }}
            >
              {/* Close Button */}
              <IconButton
                onClick={() => setMobileMenuOpen(false)}
                sx={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  color: '#44D482',
                }}
              >
                <CloseIcon />
              </IconButton>

              {/* Mobile Nav Links */}
              <Stack spacing={4} alignItems="center">
                {navItems.map((item, index) => (
                  <motion.a
                    key={item.label}
                    href={item.href}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    onClick={() => setMobileMenuOpen(false)}
                    style={{ textDecoration: 'none' }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Bebas Neue, sans-serif',
                        fontSize: '2.5rem',
                        color: 'rgba(255, 255, 255, 0.9)',
                        transition: 'color 0.2s ease',
                        '&:hover': {
                          color: '#44D482',
                        },
                      }}
                    >
                      {item.label}
                    </Typography>
                  </motion.a>
                ))}
              </Stack>

              {/* Mobile Social Icons */}
              <Stack
                direction="row"
                spacing={2}
                sx={{ mt: 6 }}
              >
                {socialLinks.map((social, index) => (
                  <motion.div
                    key={social.label}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
                  >
                    <IconButton
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '1.5rem',
                        '&:hover': {
                          color: '#44D482',
                        },
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  </motion.div>
                ))}
              </Stack>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
